import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import HeroProfessional from '@/components/HeroProfessional';
import { ScrollReveal } from '@/components/ScrollReveal';
import { 
  ArrowRight, 
  Bike,
  Building2,
  CheckCircle,
  Settings,
  Shield,
  Users,
  Wrench,
  Star,
  Target,
  Zap,
  Award,
  User,
  MessageSquare,
  Phone,
  Mail,
  FileText,
  Cog,
  Lightbulb,
  TrendingUp,
  Clock,
  Heart,
  Gauge
} from 'lucide-react';

const AchatParcsVelo = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <HeroProfessional
        title="Achat de parcs vélo"
        subtitle="Des vélos conçus pour l'exploitation professionnelle et l'usage intensif"
        keyPoints={[
          { icon: Cog, text: "Conception interne" },
          { icon: Shield, text: "Durabilité professionnelle" },
          { icon: Target, text: "Optimisation terrain" }
        ]}
        backgroundImage="./images/professional_services_hero_20260110_101615_20260113_161851.png"
        imageAlt="Parcs vélo professionnels Les Hirondelles"
        actionButtons={[
          {
            text: "Demander une étude de parc vélo",
            href: "#consultation",
            type: "primary",
            title: "Obtenir une consultation personnalisée",
            ariaLabel: "Demander une étude pour votre parc vélo"
          },
          {
            text: "Voir nos modèles",
            href: "#modeles-disponibles",
            type: "secondary",
            title: "Découvrir notre gamme de vélos",
            ariaLabel: "Consulter les modèles de vélos disponibles"
          }
        ]}
      />

      {/* 1. INTRO — DES VÉLOS CONÇUS POUR L'EXPLOITATION */}
      <section className="py-16 bg-gradient-to-b from-stone-50/30 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-stone-200">
              <h2 className="text-3xl font-bold text-foreground mb-6">
                Des parcs vélo pensés pour l'usage professionnel
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Les Hirondelles conçoivent et développent des vélos spécifiquement 
                  pour l'exploitation professionnelle.
                </p>
                <p>
                  Ces vélos ne sont pas des produits grand public adaptés après coup. 
                  Ils sont conçus à partir de contraintes opérationnelles réelles.
                </p>
                <div className="bg-primary/5 rounded-lg p-6 border-l-4 border-primary">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <Lightbulb className="h-8 w-8 text-primary mx-auto mb-2" />
                      <p className="font-semibold text-foreground">Conception interne</p>
                    </div>
                    <div>
                      <Cog className="h-8 w-8 text-primary mx-auto mb-2" />
                      <p className="font-semibold text-foreground">Premières séries de production</p>
                    </div>
                    <div>
                      <Shield className="h-8 w-8 text-primary mx-auto mb-2" />
                      <p className="font-semibold text-foreground">Partenaire industriel</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 2. A STRONG FOCUS ON CHILDREN BIKES */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Des vélos enfants réellement optimisés
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <ScrollReveal>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Les vélos enfants ont été l'un des produits les plus travaillés et optimisés. 
                    L'objectif était l'accessibilité, la sécurité et la durabilité.
                  </p>
                  
                  <div className="bg-white rounded-lg p-6 border border-stone-200">
                    <h4 className="font-semibold text-foreground mb-4">Travail d'optimisation :</h4>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Géométrie de cadre optimisée pour l'enjambement facile</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Tailles adaptées (20" et 24")</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Cadres aluminium légers</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Tiges de selle aluminium</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Pédales aluminium</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Composants simplifiés pour réduire la maintenance</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Pièces choisies pour la résistance à l'usage intensif</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-primary/5 rounded-lg p-6 border-l-4 border-primary">
                    <p className="text-foreground font-semibold">
                      Chaque choix de composant a été fait pour simplifier l'usage, 
                      réduire les pannes et allonger la durée de vie.
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
            
            <div className="relative">
              <ScrollReveal delay={200}>
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8">
                  <div className="text-center space-y-6">
                    <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto">
                      <User className="h-10 w-10 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Focus vélos enfants</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/60 rounded-lg p-4 text-center">
                        <Gauge className="h-6 w-6 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium text-foreground">20" & 24"</p>
                        <p className="text-xs text-muted-foreground">Tailles optimisées</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-4 text-center">
                        <Shield className="h-6 w-6 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium text-foreground">Aluminium</p>
                        <p className="text-xs text-muted-foreground">Léger & durable</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-4 text-center">
                        <Target className="h-6 w-6 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium text-foreground">Accessibilité</p>
                        <p className="text-xs text-muted-foreground">Enjambement facile</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-4 text-center">
                        <Wrench className="h-6 w-6 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium text-foreground">Maintenance</p>
                        <p className="text-xs text-muted-foreground">Simplifiée</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* 3. DES VÉLOS QUI ÉVOLUENT AVEC L'USAGE */}
      <section className="py-16 bg-muted/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Des vélos qui évoluent avec l'usage
            </h2>
          </ScrollReveal>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-stone-200">
            <ScrollReveal delay={100}>
              <div className="space-y-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="h-8 w-8 text-primary" />
                  </div>
                  <p className="text-xl text-foreground font-semibold mb-6">
                    Ces vélos ont évolué grâce à l'usage réel
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-foreground mb-2">Retours clients</h4>
                    <p className="text-muted-foreground text-sm">Feedback terrain continu</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Settings className="h-6 w-6 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-foreground mb-2">Contraintes opérationnelles</h4>
                    <p className="text-muted-foreground text-sm">Adaptation aux usages réels</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Wrench className="h-6 w-6 text-orange-600" />
                    </div>
                    <h4 className="font-semibold text-foreground mb-2">Expérience maintenance</h4>
                    <p className="text-muted-foreground text-sm">Optimisation continue</p>
                  </div>
                </div>
                
                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <div className="text-center space-y-4">
                    <h4 className="font-semibold text-foreground">Processus de conception itératif</h4>
                    <div className="flex items-center justify-center space-x-4 text-muted-foreground">
                      <span>Ancré dans la réalité terrain</span>
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Pas d'ingénierie théorique</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 4. AVAILABLE MODELS */}
      <section id="modeles-disponibles" className="py-16 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Modèles disponibles
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <ScrollReveal delay={100}>
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <Badge variant="secondary" className="mb-2">20 pouces</Badge>
                    <h3 className="text-xl font-semibold text-foreground">
                      VTC enfants 20"
                    </h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      Conçu pour les flottes partagées
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      Durabilité de niveau professionnel
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      Géométrie optimisée
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      Composants simplifiés
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <Badge variant="secondary" className="mb-2">24 pouces</Badge>
                    <h3 className="text-xl font-semibold text-foreground">
                      VTC enfants 24"
                    </h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      Conçu pour les flottes partagées
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      Durabilité de niveau professionnel
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      Cadre aluminium léger
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      Maintenance réduite
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 5. WHO IS THIS FOR */}
      <section className="py-16 bg-muted/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              À qui s'adresse cette offre
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ScrollReveal delay={100}>
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-stone-200">
                <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Campings</h4>
                <p className="text-muted-foreground text-sm">
                  Établissements souhaitant exploiter leur propre flotte vélo
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-stone-200">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Hébergements touristiques</h4>
                <p className="text-muted-foreground text-sm">
                  Résidences, villages vacances, centres de loisirs
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-stone-200">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Collectivités locales</h4>
                <p className="text-muted-foreground text-sm">
                  Communes, intercommunalités, offices de tourisme
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={400}>
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-stone-200">
                <Zap className="h-12 w-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Opérateurs professionnels</h4>
                <p className="text-muted-foreground text-sm">
                  Entreprises de services, loueurs indépendants
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={500}>
              <div className="bg-white rounded-xl p-6 text-center shadow-md border border-stone-200 md:col-span-2 lg:col-span-2">
                <Target className="h-12 w-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Structures souhaitant exploiter leur propre flotte vélo</h4>
                <p className="text-muted-foreground text-sm">
                  Organisations ayant la capacité et la volonté de gérer en interne leur activité vélo
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 6. BEYOND THE BIKE — OPTIONAL SERVICES */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Au-delà du vélo : services optionnels
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <ScrollReveal>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Les professionnels peuvent acquérir une flotte vélo et l'exploiter en interne, 
                    avec la possibilité de souscrire optionnellement à :
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Wrench className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Services de maintenance</h4>
                        <p className="text-muted-foreground">Entretien régulier et réparations</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Settings className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Révision complète de fin de saison</h4>
                        <p className="text-muted-foreground">Remise en état pour la saison suivante</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Support technique</h4>
                        <p className="text-muted-foreground">Accompagnement et conseils</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
            
            <div className="relative">
              <ScrollReveal delay={200}>
                <div className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-2xl p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Partenaire long terme</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-white/60 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary" />
                        <span className="text-muted-foreground">Pas seulement un fournisseur</span>
                      </div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary" />
                        <span className="text-muted-foreground">Accompagnement dans la durée</span>
                      </div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary" />
                        <span className="text-muted-foreground">Expertise terrain partagée</span>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* 7. CALL TO ACTION — CONSULTATION */}
      <section id="consultation" className="py-16 bg-gradient-to-br from-primary/5 to-primary/10 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <div className="space-y-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <MessageSquare className="h-8 w-8 text-primary" />
              </div>
              
              <h2 className="text-3xl font-bold text-foreground">
                Demander une étude de parc vélo
              </h2>
              
              <div className="space-y-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                <p>
                  Pas de vente en ligne. Nous privilégions une approche par consultation 
                  pour des propositions adaptées à vos besoins spécifiques.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <FileText className="h-6 w-6 text-primary mx-auto mb-2" />
                    <p className="font-medium text-foreground">Pas de vente en ligne</p>
                  </div>
                  <div>
                    <Users className="h-6 w-6 text-primary mx-auto mb-2" />
                    <p className="font-medium text-foreground">Approche par consultation</p>
                  </div>
                  <div>
                    <Target className="h-6 w-6 text-primary mx-auto mb-2" />
                    <p className="font-medium text-foreground">Propositions adaptées</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                  <Link to="/nous-contacter">
                    <MessageSquare className="mr-2 h-5 w-5" />
                    Demander une étude de parc vélo
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/professionnels">
                    Retour aux solutions professionnelles
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
};

export default AchatParcsVelo;